import { useEffect, useRef, useState } from 'react';

type NativeProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type'
>;

export interface NumberInputProps extends NativeProps {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  /**
   * When false, clearing the field resolves to 0 (or `min`) on blur instead of
   * null. Use for fields that must always hold a number (e.g. point allocations).
   */
  allowEmpty?: boolean;
}

/**
 * A number input you can actually clear.
 *
 * The problem with `value={num}` + `onChange={e => setNum(Number(e.target.value) || 0)}`
 * is that emptying the field coerces straight back to 0, so select-all →
 * backspace → type is impossible. This keeps the *displayed text* as local
 * state (so intermediate/empty states are allowed) and only normalizes on blur.
 * It never clobbers what you're typing: external value updates are ignored while
 * the field is focused.
 */
export function NumberInput({
  value,
  onChange,
  min,
  max,
  allowEmpty = true,
  onFocus,
  onBlur,
  ...rest
}: NumberInputProps) {
  const [text, setText] = useState(value == null ? '' : String(value));
  const focused = useRef(false);

  // Sync from the outside only when we're not the ones editing.
  useEffect(() => {
    if (focused.current) return;
    setText(value == null ? '' : String(value));
  }, [value]);

  const clamp = (n: number) => {
    let x = n;
    if (typeof min === 'number') x = Math.max(min, x);
    if (typeof max === 'number') x = Math.min(max, x);
    return x;
  };

  return (
    <input
      {...rest}
      type="number"
      inputMode="numeric"
      min={min}
      max={max}
      value={text}
      onFocus={(e) => {
        focused.current = true;
        onFocus?.(e);
      }}
      onChange={(e) => {
        const raw = e.target.value;
        setText(raw);
        if (raw.trim() === '') {
          onChange(allowEmpty ? null : 0);
          return;
        }
        const n = Number(raw);
        if (!Number.isNaN(n)) onChange(n); // don't clamp mid-type — it fights typing
      }}
      onBlur={(e) => {
        focused.current = false;
        const raw = e.target.value.trim();
        if (raw === '') {
          if (allowEmpty) {
            onChange(null);
            setText('');
          } else {
            const z = clamp(0);
            onChange(z);
            setText(String(z));
          }
        } else {
          const n = clamp(Number(raw));
          const resolved = Number.isNaN(n) ? (allowEmpty ? null : clamp(0)) : n;
          onChange(resolved);
          setText(resolved == null ? '' : String(resolved));
        }
        onBlur?.(e);
      }}
    />
  );
}
